import styled, { css, keyframes } from "styled-components";

interface StyleProps {
  color?: string;
  templateColumns?: string;
}

interface IsMobileProps {
  isMobile?: boolean;
}

// Keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// Page Container
export const PageWrapper = styled.div`
  background: #F3ECE1;
  height: 100vh;
  overflow: hidden;

  @media (max-width: 1024px) {
    height: auto;
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

// Hero Section
export const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  padding: 40px clamp(20px, 4.5vw, 64px) 40px;
  overflow-y: hidden;
  background: #F3ECE1;

  @media (max-width: 1024px) {
    height: auto;
    min-height: 100vh;
    padding: 120px clamp(20px, 4.5vw, 64px) 60px;
    overflow-y: visible;
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    padding: 120px 32px 60px;
  }
`;

export const HeroContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: start;
  max-width: 1312px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 64px;
  }

  @media (max-width: 768px) {
    gap: 48px;
  }
`;

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 80px;

  @media (max-width: 1024px) {
    align-items: center;
    text-align: center;
    margin-top: 0;
  }
`;

export const HeroTagline = styled.span`
  display: block;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 400;
  color: #D9442B;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  margin-bottom: 8px;
`;

export const HeroTitle = styled.h1`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(48px, 5.5vw, 72px);
  font-weight: 400;
  color: #1A1814;
  line-height: 1.0;
  letter-spacing: -0.02em;
  margin: 0;
  text-wrap: balance;

  em {
    color: #D9442B;
    font-style: italic;
  }
`;

export const HeroDescription = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  font-weight: 400;
  color: #4A453D;
  line-height: 1.5;
  margin: 0;
  max-width: 30em;
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  margin-top: 8px;

  @media (max-width: 1024px) {
    flex-direction: column;
    width: 100%;
    gap: 16px;
    max-width: 400px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const PrimaryButton = styled.button`
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  background: #1A1814;
  color: #F3ECE1;
  border: none;
  padding: 17px 26px;
  border-radius: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;

  &::after {
    content: '';
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23D9442B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12h14'/%3E%3Cpath d='m12 5 7 7-7 7'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    transition: margin-left 0.3s ease;
  }

  &:hover {
    background: #0a0906;

    &::after {
      margin-left: 6px;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

export const SecondaryButton = styled.button`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  font-weight: 400;
  background: transparent;
  color: #1A1814;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: #D9442B;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 4px;
  transition: all 0.3s ease;

  &:hover {
    text-decoration-thickness: 2.5px;
  }

  @media (max-width: 768px) {
    width: auto;
  }
`;

export const HeroImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 1024px) {
    order: -1;
    justify-content: center;
    width: 100%;
  }
`;

export const HeroImageCircle = styled.div`
  position: relative;
  max-width: 544px;

  @media (max-width: 1024px) {
    max-width: 460px;
    width: 100%;
  }

  @media (max-width: 768px) {
    max-width: 380px;
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

// Hero Stats Section
export const HeroStatsSection = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #1A1814;
  max-width: 900px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 32px;
    max-width: 100%;
    width: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px 24px;
  }
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid #D9442B;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

export const StatValue = styled.div`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(24px, 2.5vw, 32px);
  font-weight: 400;
  color: #1A1814;
  line-height: 1.0;
  letter-spacing: -0.01em;
`;

export const StatLabel = styled.div`
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 10px;
  font-weight: 400;
  color: #6B645A;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  line-height: 1.4;
  transition: color 0.3s ease;

  ${StatItem}:hover & {
    color: #D9442B;
  }
`;

// Floating decorative elements
export const FloatingDot = styled.div<{ top?: string; left?: string; right?: string; bottom?: string; delay?: number; color?: string }>`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color || '#D9442B'};
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${props => props.delay || 0}s;
  opacity: 0.6;
`;

// Marquee Banner
export const MarqueeBanner = styled.div`
  background: #1A1814;
  padding: 24px 0;
  overflow: hidden;
  position: relative;
  z-index: 2;
`;

export const MarqueeTrack = styled.div`
  display: flex;
  animation: ${scroll} 20s linear infinite;
`;

export const MarqueeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
  white-space: nowrap;
  padding: 0 24px;
`;

export const MarqueeItem = styled.span`
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #F3ECE1;
  display: flex;
  align-items: center;
  gap: 16px;

  &::after {
    content: '•';
    color: #D9442B;
  }
`;

// About Section
export const AboutSection = styled.section`
  padding: clamp(40px, 8vw, 80px) clamp(20px, 4.5vw, 64px);
  position: relative;
  background: #F3ECE1;
`;

export const AboutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  max-width: 1312px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

export const AboutImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;

  ${HeroImageCircle} {
    max-width: 544px;

    @media (max-width: 1024px) {
      max-width: 460px;
    }

    @media (max-width: 768px) {
      max-width: 380px;
    }
  }

  @media (max-width: 1024px) {
    min-height: 400px;
    display: none;
  }
`;

export const SkillTree = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
`;

export const SkillTreeRoot = styled.div`
  position: relative;
  padding: 14px 28px;
  border-radius: 50px;
  background: linear-gradient(135deg, rgba(245, 217, 204, 0.2) 0%, rgba(245, 217, 204, 0.2) 100%);
  border: 2px solid #F5D9CC;
  color: #F5D9CC;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  box-shadow: 0 0 30px rgba(245, 217, 204, 0.3);
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 40px;
    background: linear-gradient(to bottom, #F5D9CC, rgba(245, 217, 204, 0.3));
  }
`;

export const SkillTreeBranches = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;
  max-width: 600px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    left: 16.66%;
    right: 16.66%;
    height: 40px;
    border-top: 2px solid rgba(245, 217, 204, 0.3);
    border-left: 2px solid rgba(245, 217, 204, 0.3);
    border-right: 2px solid rgba(245, 217, 204, 0.3);
    border-radius: 20px 20px 0 0;
  }
`;

export const SkillTreeNode = styled.div<{ delay?: number }>`
  position: relative;
  padding: 12px 20px;
  border-radius: 50px;
  background: rgba(20, 20, 30, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(245, 217, 204, 0.4);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  animation: ${float} 5s ease-in-out infinite;
  animation-delay: ${props => props.delay || 0}s;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 32px;
    background: linear-gradient(to top, rgba(245, 217, 204, 0.4), transparent);
  }

  &:hover {
    border-color: #F5D9CC;
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(245, 217, 204, 0.4);
  }
`;

export const SkillTreeLeaves = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  max-width: 600px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -32px;
    left: 16.66%;
    right: 16.66%;
    height: 32px;
    border-top: 2px solid rgba(245, 217, 204, 0.3);
    border-left: 2px solid rgba(245, 217, 204, 0.3);
    border-right: 2px solid rgba(245, 217, 204, 0.3);
    border-radius: 16px 16px 0 0;
  }
`;

export const SkillTreeLeaf = styled.div<{ delay?: number }>`
  position: relative;
  padding: 10px 16px;
  border-radius: 50px;
  background: rgba(20, 20, 30, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #a0a0a0;
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  animation: ${float} 5s ease-in-out infinite;
  animation-delay: ${props => props.delay || 0}s;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 24px;
    background: linear-gradient(to top, rgba(245, 217, 204, 0.3), transparent);
  }

  &:hover {
    border-color: rgba(245, 217, 204, 0.6);
    color: #ffffff;
    transform: translateY(-2px);
  }
`;

// Floating Skill Tags
export const SkillTag = styled.div<{ top?: string; left?: string; right?: string; bottom?: string; delay?: number }>`
  position: absolute;
  background: rgba(20, 20, 30, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 18px;
  border-radius: 50px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  animation: ${float} 5s ease-in-out infinite;
  animation-delay: ${props => props.delay || 0}s;
  z-index: 2;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    width: 60px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(245, 217, 204, 0.4) 50%,
      rgba(245, 217, 204, 0.15) 100%
    );
    top: 50%;
    transform: translateY(-50%);
    z-index: -1;
  }

  /* Position connector based on tag position */
  ${props => props.left && `
    &::before {
      left: 100%;
      margin-left: 4px;
    }
  `}

  ${props => props.right && !props.left && `
    &::before {
      right: 100%;
      margin-right: 4px;
      background: linear-gradient(
        90deg,
        rgba(245, 217, 204, 0.15) 0%,
        rgba(245, 217, 204, 0.4) 50%,
        transparent 100%
      );
    }
  `}

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 8px 14px;
  }

  @media (max-width: 1390px) {
    display: none;
  }
`;

export const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 40px;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    padding-top: 0;
  }
`;

export const AboutTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(40px, 4.8vw, 68px);
  font-weight: 400;
  color: #1A1814;
  line-height: 1.0;
  letter-spacing: -0.02em;
  margin: 0;
  text-wrap: balance;
`;

export const AboutSubtitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #D9442B;
  margin: 0;
`;

export const AboutDescription = styled.p`
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  font-weight: 400;
  color: #4A453D;
  line-height: 1.5;
  margin: 0;
  max-width: 30em;

  & + & {
    margin-top: 8px;
  }
`;

// Competencies Grid (replaces skill bars)
export const CompetenciesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
`;

export const CompetencyTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background: rgba(217, 68, 43, 0.15);
  border: 1px solid rgba(217, 68, 43, 0.3);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #D9442B;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(217, 68, 43, 0.25);
    border-color: rgba(217, 68, 43, 0.5);
  }
`;

export const AboutCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #D9442B 0%, #F5D9CC 100%);
  color: #1A1814;
  border: none;
  padding: 14px 28px;
  border-radius: 50px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  margin-top: 4px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(245, 217, 204, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

export const SocialLinksLabel = styled.span`
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  font-weight: 400;
  color: #8A8276;
  display: block;
  margin-top: 32px;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.20em;
`;

export const SocialLinksWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

export const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid #D2C8B8;
  transition: all 0.3s ease;

  img {
    width: 22px;
    height: 22px;
    filter: brightness(0);
  }

  &:hover {
    background: #D9442B;
    border-color: #D9442B;
    transform: translateY(-2px);

    img {
      filter: brightness(0) invert(1);
    }
  }
`;

// Testimonials Section
export const TestimonialsSection = styled.section`
  padding: clamp(80px, 10vw, 140px) clamp(20px, 4.5vw, 64px);
  background: #F3ECE1;
`;

export const TestimonialsContainer = styled.div`
  max-width: 1312px;
  margin: 0 auto;
`;

// Case Study Section
export const CaseStudySection = styled.section`
  padding: 80px 64px;
  background: rgba(15, 15, 25, 0.6);
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    padding: 60px 36px;
  }

  @media (max-width: 768px) {
    padding: 48px 24px;
  }
`;

export const CaseStudyContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

export const CaseStudySectionHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

export const CaseStudySectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const CaseStudySectionSubtitle = styled.p`
  font-size: 1.0625rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

export const CaseStudyCard = styled.a`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  background: linear-gradient(135deg, rgba(217, 68, 43, 0.1) 0%, rgba(245, 217, 204, 0.05) 100%);
  border: 1px solid rgba(217, 68, 43, 0.2);
  border-radius: 24px;
  padding: 48px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(217, 68, 43, 0.15);
    border-color: rgba(217, 68, 43, 0.4);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px;
  }
`;

export const CaseStudyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CaseStudyTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(245, 217, 204, 0.15);
  border: 1px solid rgba(245, 217, 204, 0.3);
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #F5D9CC;
  width: fit-content;
`;

export const CaseStudyTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const CaseStudyDescription = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const CaseStudyMetrics = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 16px;

  @media (max-width: 768px) {
    gap: 24px;
    flex-wrap: wrap;
  }
`;

export const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetricValue = styled.span`
  font-size: 1.75rem;
  font-weight: 700;
  color: #D9442B;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const MetricLabel = styled.span`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
`;

export const CaseStudyImageWrapper = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 1024px) {
    order: -1;
  }
`;

export const CaseStudyImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

export const ViewCaseStudyLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #D9442B;
  font-weight: 600;
  font-size: 1rem;
  margin-top: 8px;
  transition: gap 0.2s ease;

  &:hover {
    gap: 12px;
  }
`;

// Legacy exports for compatibility
export const SectionContainer = styled.section<StyleProps>(
  ({ color }: StyleProps) => css`
    background-color: ${color || '#1A1814'};
  `
);

export const Section = styled.div<StyleProps>(
  ({ color, templateColumns }: StyleProps) => css`
    background-color: ${color || '#1A1814'};
    display: grid;
    grid-template-columns: ${templateColumns || '1fr'};
    align-items: center;
    padding: 36px;

    @media screen and (max-width: 990px) {
      grid-template-columns: 1fr;
    }
  `
);

export const UniqueSection = styled.div<IsMobileProps>`
  display: none;
`;

export const Separator = styled.img`
  width: 100%;
  position: relative;
  rotate: 180deg;
  top: 4px;
`;

export const BottomSeparator = styled.img`
  width: 100%;
  position: relative;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

export const Text = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 32px;
`;

export const TextContent = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.8;
`;
